import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import api from "../api/axios";
import { X, Send, Loader2, Search, User, Phone } from "lucide-react";

const PROVIDER_API_PATH = "/provider"; // GET /api/provider -> getAll providers
const TRANSFER_API_PATH = "/provider"; // POST /api/provider/transfer

const ProviderTransferModal = ({ open, order, close, onSuccess }) => {
    const [providers, setProviders] = useState([]);
    const [providersLoading, setProvidersLoading] = useState(false);
    const [providerSearch, setProviderSearch] = useState("");

    const [selectedProvider, setSelectedProvider] = useState(null); // full provider object
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);

    // fetch providers list jab modal open ho
    useEffect(() => {
        if (!open) return;

        const fetchProviders = async () => {
            setProvidersLoading(true);
            try {
                const res = await api.get(`${PROVIDER_API_PATH}`);
                const list = res.data?.data || res.data || [];
                setProviders(Array.isArray(list) ? list : []);
            } catch (err) {
                Swal.fire({
                    icon: "error",
                    title: "Failed to load providers",
                    text: err.response?.data?.message || "Check connection",
                });
            } finally {
                setProvidersLoading(false);
            }
        };

        fetchProviders();
    }, [open]);

    // reset state jab modal close ho ya order badle
    useEffect(() => {
        if (!open) {
            setSelectedProvider(null);
            setReason("");
            setProviderSearch("");
        }
    }, [open]);

    if (!open || !order) return null;

    // current owner ko list se exclude kro, aur inactive providers bhi hide kro
    const filteredProviders = providers.filter((p) => {
        if (p.active === false) return false;
        if (p.whatsapp_number === order.provider_number) return false;

        if (!providerSearch.trim()) return true;
        const q = providerSearch.trim().toLowerCase();
        return (
            p.name?.toLowerCase().includes(q) ||
            p.whatsapp_number?.includes(q) ||
            p.callingNumber?.includes(q)
        );
    });

    const handleTransfer = async () => {
        if (!selectedProvider) {
            return Swal.fire("Required", "Select a provider to transfer to", "warning");
        }

        setLoading(true);
        try {
            await api.post(`${TRANSFER_API_PATH}/transfer`, {
                orderId: order.id,
                new_provider: selectedProvider.whatsapp_number,
                reason,
            });
            Swal.fire("Transferred", `Order sent to ${selectedProvider.name}`, "success");
            onSuccess?.();
        } catch (err) {
            Swal.fire("Error", err.response?.data?.message || "Transfer failed", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-5">
                    <h3 className="text-base font-semibold flex items-center gap-2">
                        <Send size={18} className="text-green-600" /> Transfer order
                    </h3>
                    <button onClick={close} className="p-1 hover:bg-gray-100 rounded-lg">
                        <X size={18} />
                    </button>
                </div>

                <p className="text-xs text-gray-500 mb-4">
                    Currently with: <span className="font-mono">{order.provider_number}</span>
                </p>

                {/* Provider search */}
                <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-600 mb-1.5">
                        Search provider
                    </label>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                            type="text"
                            value={providerSearch}
                            disabled={loading}
                            onChange={(e) => setProviderSearch(e.target.value)}
                            placeholder="Search by name or number…"
                            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm disabled:opacity-50"
                        />
                    </div>
                </div>

                {/* Provider list */}
                <div className="mb-4 border border-gray-200 rounded-lg max-h-52 overflow-y-auto divide-y divide-gray-100">
                    {providersLoading ? (
                        <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
                            <Loader2 size={18} className="animate-spin mr-2" /> Loading providers…
                        </div>
                    ) : filteredProviders.length === 0 ? (
                        <div className="text-center py-8 text-gray-400 text-sm">No providers found</div>
                    ) : (
                        filteredProviders.map((p) => {
                            const isSelected = selectedProvider?._id === p._id;
                            return (
                                <button
                                    key={p._id}
                                    type="button"
                                    disabled={loading}
                                    onClick={() => setSelectedProvider(p)}
                                    className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition disabled:opacity-50 ${isSelected ? "bg-green-50" : "hover:bg-gray-50"
                                        }`}
                                >
                                    <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isSelected ? "bg-green-600 text-white" : "bg-gray-100 text-gray-500"
                                            }`}
                                    >
                                        <User size={16} />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                                        <p className="text-xs text-gray-400 font-mono flex items-center gap-1">
                                            <Phone size={10} /> {p.whatsapp_number}
                                        </p>
                                    </div>
                                    {isSelected && (
                                        <span className="ml-auto text-xs font-semibold text-green-700">Selected</span>
                                    )}
                                </button>
                            );
                        })
                    )}
                </div>

                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-600 mb-1.5">
                        Reason (optional)
                    </label>
                    <input
                        type="text"
                        value={reason}
                        disabled={loading}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Why transferring?"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
                    />
                </div>

                <div className="flex justify-end gap-3">
                    <button
                        onClick={close}
                        disabled={loading}
                        className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleTransfer}
                        disabled={loading || !selectedProvider}
                        className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={16} className="animate-spin" /> Transferring…
                            </>
                        ) : (
                            "Transfer"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProviderTransferModal;